import type { ActionFunctionArgs, LoaderFunctionArgs } from "@remix-run/node";

import { json, redirect } from "@remix-run/node";
import { Form, Link, useActionData, useNavigation } from "@remix-run/react";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { getClientIPAddress } from "remix-utils/get-client-ip-address";
import { z } from "zod";

import { Heading, Region } from "~/components/heading";
import { GoogleIcon } from "~/components/icons";
import { getPasswordConditions } from "~/components/signup/get-password-conditions";
import { getPasswordStrength } from "~/components/signup/get-password-strength";
import { returnTo } from "~/components/signup/return-to";
import { api } from "~/services/api.server";
import {
  auth,
  getAuthErrorAlertProps,
  getAuthErrorMessage,
} from "~/services/auth.server";
import { Auth0Service } from "~/services/auth0.server";
import { CSRFInput, readCSRFToken, validateCSRFToken } from "~/services/csrf";
import { i18n } from "~/services/i18n.server";
import { Logger } from "~/services/logger.server";
import {
  commitSession,
  getSession,
  sessionStorage,
} from "~/services/session.server";
import type { PasswordConditions } from "~/types";
import type { StaticAlertProps } from "~/ui/shadcn/alert";
import { StaticAlert } from "~/ui/shadcn/alert";
import { Button } from "~/ui/shadcn/button";
import { Field, FieldLabel } from "~/ui/shadcn/field";
import { Input } from "~/ui/shadcn/input";
import { getFormData } from "~/utils/get-form-data";
import { toStatic } from "~/utils/toStatic";

export async function loader({ request }: LoaderFunctionArgs) {
  await auth.isAuthenticated(request, { successRedirect: "/dashboard" });
  return null;
}

export async function action({ request }: ActionFunctionArgs) {
  let logger = Logger.getLogger("routes/signup#action");

  let session = await getSession(request);

  if (session.has("auth:error")) {
    let error = await getAuthErrorAlertProps(
      session.get("auth:error") as unknown,
      request,
    );
    if (error) {
      session.unset("auth:error");
      let headers = new Headers();
      headers.append("Set-Cookie", await commitSession(session));
      if (error) return json(error, { status: 400, headers });
    }
  }
  let t = await i18n.getFixedT(request);
  let auth0 = new Auth0Service(t);

  try {
    let csrf = await readCSRFToken(request);
    if (!csrf) throw json("Invalid CSRF token.", 422);

    let formData = await getFormData(request);

    validateCSRFToken(csrf, formData);

    if (auth0.isCommonPassword(formData.get("password"))) {
      throw new Error("COMMON_PASSWORD");
    }

    let ipAddress = getClientIPAddress(request);

    let registerResult = await auth0.register(formData, ipAddress);

    let isRegistrationSuccess = registerResult.status === "success";

    let result = await auth0.credentials(formData, ipAddress);

    if (result.status === "failure") {
      if (isRegistrationSuccess) {
        throw new Error(
          `Account created but credentials failure: ${JSON.stringify(
            result.error,
          )}`,
        );
      }

      // likely the account exists (i.e. registration failed), and the login failed (i.e. they used a bad password)
      return json<StaticAlertProps>({
        type: "danger",
        title: t("Couldn't sign in") as string,
        body: t("Wrong email or password.") as string,
      });
    }

    let { token } = await api().auth.signIn(result.jwt);

    let session = await getSession(request);
    session.set(auth.sessionKey as "adminToken", token);

    let headers = new Headers({
      "Set-Cookie": await sessionStorage.commitSession(session),
    });

    return redirect(await returnTo(request, headers), { headers });
  } catch (exception: unknown) {
    const type = "danger";
    const title = t("There is a problem");

    if (exception instanceof Error && exception.message === "COMMON_PASSWORD") {
      return json<StaticAlertProps>(
        {
          type,
          title,
          body: t(
            "This password is very common. Please choose one that is more secure.",
          ) as string,
        },
        400,
      );
    }

    if (exception instanceof z.ZodError) {
      return json<StaticAlertProps>({
        type,
        title,
        body: exception?.issues?.map(({ message }) => message),
      });
    }

    let authErrorMsg = getAuthErrorMessage(exception);
    if (authErrorMsg) {
      let alertProps = await getAuthErrorAlertProps(
        { message: authErrorMsg },
        request,
      );
      if (alertProps) {
        return json<StaticAlertProps>(alertProps);
      }
    }

    logger.error(exception);

    return json<StaticAlertProps>({
      type,
      title,
      body: t("We couldn't create your account. Please try again.") as string,
      link: {
        label: t("Contact Support") as string,
        href: "mailto:support@daffy.org",
      },
    });
  }
}

export default function SignUp() {
  let { t } = useTranslation();
  let alertProps = useActionData<StaticAlertProps | undefined>() as
    | StaticAlertProps
    | undefined;

  const [isWeakPassword, setIsWeakPassword] = useState(true);
  const [allowEmailSignup, setAllowEmailSignup] = useState(false);

  let navigation = useNavigation();
  let isSubmitting = navigation.state !== "idle";

  return (
    <div className="min-h-screen sm:py-10 w-full scheme-light bg-muted xl:py-24 flex sm:items-start sm:justify-center -xl:flex-col -sm:min-h-screen">
      <figure className="hidden lg:block">
        <img
          src={toStatic("/auth0-login/dfw-login.webp")}
          alt={t("Daffy - A smarter way to give")}
          className="w-full max-w-130 h-auto -xl:hidden"
          width={520}
          height={604}
        />
      </figure>

      <Region className="py-12 px-[60px] sm:rounded-xl sm:shadow max-w-130 -xl:mx-auto flex flex-col gap-6 xl:gap-8 bg-card -sm:min-h-screen">
        <header className="-xl:text-center flex flex-col gap-2">
          <Heading className="text-3xl leading-9 font-medium">
            <span>{t("Create a Daffy for Work admin account")}</span>
          </Heading>

          <p className="text-muted-foreground text-sm leading-5">
            {t(
              "Once you create an account, you’ll be able to customize your giving program in minutes and launch it today!",
            )}
          </p>

          <div className="mt-4">
            <StaticAlert
              type="info"
              body={t(
                "Please use your work email to sign up. Do not use your personal email.",
              )}
            />
          </div>
        </header>

        <Form method="post" action="/login" className="flex -xl:flex-col gap-3">
          <CSRFInput />
          <button
            name="intent"
            value="google"
            type="submit"
            className="border border-input bg-card hover:bg-muted py-3 px-3.5 xl:px-3 gap-2 flex items-center justify-center leading-none font-normal rounded-md w-full xl:text-sm xl:leading-5 whitespace-nowrap"
          >
            <GoogleIcon />
            <span>{t("Continue with Google")}</span>
          </button>
        </Form>
        <div className="relative my-2">
          <div
            className="absolute left-0 right-0 flex items-center justify-center"
            style={{
              transform: "translateY(-50%)",
            }}
          >
            <span className="bg-card px-4 text-sm">{t("or")}</span>
          </div>
          <hr />
        </div>

        {allowEmailSignup ? (
          <Form method="post" className="contents">
            <CSRFInput />

            <EmailField />

            <PasswordField
              isWeakPassword={isWeakPassword}
              onChange={setIsWeakPassword}
            />

            {!!alertProps && <StaticAlert {...alertProps} />}

            <Button
              type="submit"
              className="rounded-full"
              disabled={isSubmitting}
            >
              {t("Continue with Email")}
            </Button>
          </Form>
        ) : (
          <div className="flex justify-center">
            <Button
              type="button"
              variant="link"
              size="xs"
              className="text-foreground"
              onClick={() => setAllowEmailSignup(true)}
            >
              {t("Sign Up with Email")}
            </Button>
          </div>
        )}

        <footer className="flex text-sm leading-5 gap-3 justify-center items-baseline">
          <p className="font-normal">{t("Already have an account?")}</p>
          <Link to="/login" className="font-medium" prefetch="intent">
            {t("Sign in")}
          </Link>
        </footer>

        <Trans
          parent="footer"
          className="text-center text-sm px-5 w-full max-w-130"
          defaults="By signing up with email, or Google, you accept our <agreement>Employer Agreement</agreement> terms."
          components={{
            agreement: (
              // eslint-disable-next-line jsx-a11y/anchor-has-content
              <Link to="/legal/agreement" className="font-bold underline" />
            ),
          }}
        />
      </Region>
    </div>
  );
}

function EmailField() {
  let { t } = useTranslation();

  return (
    <Field>
      <FieldLabel htmlFor="email">{t("Work email address")}</FieldLabel>
      <Input id="email" type="email" name="email" autoComplete="email" />
    </Field>
  );
}

type PasswordFieldProps = {
  isWeakPassword: boolean;
  onChange: (isWeakPassword: boolean) => void;
};

function PasswordField({ isWeakPassword, onChange }: PasswordFieldProps) {
  let { t } = useTranslation();
  let [passwordConditions, setPasswordConditions] =
    useState<PasswordConditions | null>(null);

  let alertConditions: string[] = [];
  if (passwordConditions) {
    if (!passwordConditions.hasMinimumLength)
      alertConditions.push(t("At least 8 characters in length"));
    if (!passwordConditions.hasLowerCase)
      alertConditions.push(t("At least one lower case (a-z)"));
    if (!passwordConditions.hasUpperCase)
      alertConditions.push(t("At least one upper case (A-Z)"));
    if (!passwordConditions.hasNumber)
      alertConditions.push(t("At least one number (0-9)"));
    if (!passwordConditions.hasSpecialCharacter)
      alertConditions.push(t("At least one special (!#$%*@^-)"));
  }

  return (
    <Field>
      <FieldLabel htmlFor="password">{t("Password")}</FieldLabel>
      <Input
        id="password"
        type="password"
        name="password"
        autoComplete="new-password"
        onChange={({ currentTarget }) => {
          const password = currentTarget.value;
          const newPasswordConditions = getPasswordConditions(password);
          setPasswordConditions(newPasswordConditions);

          const passwordStrength = getPasswordStrength(newPasswordConditions);
          const isWeak = passwordStrength < 5;
          onChange(isWeak);
        }}
      />

      {passwordConditions &&
        (isWeakPassword ? (
          <StaticAlert
            type="warning"
            title={t("Your password must contain:")}
            body={alertConditions}
          />
        ) : (
          <StaticAlert
            type="success"
            title={t("This is a perfect password.")}
          />
        ))}
    </Field>
  );
}
